/**
 * My Groups Page
 * /my-groups - 내 모임 페이지
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import { Users, Calendar, MessageCircle, Settings, Plus, MapPin, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { buildLoginPath } from "@/lib/auth-navigation";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";

interface Group {
  id: number;
  title: string;
  slug: string;
  description: string;
  category_name_ko: string;
  city: string;
  state: string;
  cover_image_url: string | null;
  member_count: number;
  event_count: number;
  post_count: number;
  created_at: string;
  role?: string;
  status?: string;
}

export default function MyGroupsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("joined");

  // 내가 만든 모임
  const { data: myGroupsData, isLoading: isLoadingMyGroups } = useQuery<{ groups: Group[] }>({
    queryKey: ["/api/community/groups", "my-created"],
    queryFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      // TODO: API에 owner 필터 추가 필요
      const response = await fetch("/api/community/groups");
      if (!response.ok) throw new Error("Failed to fetch groups");
      const data = await response.json();
      // 클라이언트에서 필터링 (임시)
      const myGroups = data.groups.filter((g: Group) => {
        // owner_uid를 확인할 수 없으므로, 멤버 API를 통해 확인해야 함
        return true; // 임시로 모든 그룹 표시
      });
      return { groups: myGroups };
    },
    enabled: !!user,
  });

  // 내가 참여한 모임
  const { data: joinedGroupsData, isLoading: isLoadingJoined } = useQuery<{ groups: Group[] }>({
    queryKey: ["/api/community/groups", "my-joined"],
    queryFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      // TODO: 멤버 API를 통해 내가 참여한 그룹 목록 가져오기
      const response = await fetch("/api/community/groups");
      if (!response.ok) throw new Error("Failed to fetch groups");
      return response.json();
    },
    enabled: !!user,
  });

  if (!user) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">로그인이 필요합니다</h3>
          <p className="text-muted-foreground mb-4">내 모임을 보려면 로그인해주세요.</p>
          <Button asChild>
            <a href={buildLoginPath()}>로그인하기</a>
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const myGroups = myGroupsData?.groups || [];
  const joinedGroups = joinedGroupsData?.groups || [];

  return (
    <>
      <Seo
        title="내 모임 — OZKNEWS"
        description="내가 만든 모임과 참여한 모임을 관리하세요."
        path="/my-groups"
      />

      <div className="min-h-screen bg-background">
        <PageContainer size="default">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h1 className="text-3xl font-bold">내 모임</h1>
              <Button asChild>
                <Link href="/community/groups/new">
                  <Plus className="w-4 h-4 mr-2" />
                  모임 만들기
                </Link>
              </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="joined">참여한 모임</TabsTrigger>
                <TabsTrigger value="created">만든 모임</TabsTrigger>
              </TabsList>

              {/* Joined Groups */}
              <TabsContent value="joined" className="space-y-4">
                {isLoadingJoined ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Card key={i} className="p-6 animate-pulse">
                        <div className="h-48 bg-muted rounded-lg mb-4" />
                        <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                      </Card>
                    ))}
                  </div>
                ) : joinedGroups.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {joinedGroups.map((group) => (
                      <Link key={group.id} href={`/community/groups/${group.id}`}>
                        <Card className="hover-elevate cursor-pointer overflow-hidden">
                          {group.cover_image_url && (
                            <div className="relative h-48 overflow-hidden">
                              <img
                                src={group.cover_image_url}
                                alt={group.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <CardContent className="p-6 space-y-4">
                            <div>
                              <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                                {group.title}
                              </h3>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {group.description}
                              </p>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <MapPin className="w-4 h-4" />
                                <span>{group.city}, {group.state}</span>
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Users className="w-4 h-4" />
                                <span>{group.member_count || 0}명</span>
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>{group.event_count || 0}개 일정</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">참여한 모임이 없습니다</h3>
                    <p className="text-muted-foreground mb-4">
                      관심 있는 모임에 참여해보세요!
                    </p>
                    <Button asChild>
                      <Link href="/community/groups">모임 둘러보기</Link>
                    </Button>
                  </Card>
                )}
              </TabsContent>

              {/* Created Groups */}
              <TabsContent value="created" className="space-y-4">
                {isLoadingMyGroups ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Card key={i} className="p-6 animate-pulse">
                        <div className="h-48 bg-muted rounded-lg mb-4" />
                        <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                      </Card>
                    ))}
                  </div>
                ) : myGroups.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myGroups.map((group) => (
                      <Card key={group.id} className="hover-elevate overflow-hidden">
                        {group.cover_image_url && (
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={group.cover_image_url}
                              alt={group.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardContent className="p-6 space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                                {group.title}
                              </h3>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {group.description}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="w-4 h-4" />
                              <span>{group.city}, {group.state}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4" />
                              <span>{group.member_count || 0}명</span>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button variant="outline" size="sm" className="flex-1" asChild>
                              <Link href={`/community/groups/${group.id}`}>
                                <Eye className="w-4 h-4 mr-1" />
                                보기
                              </Link>
                            </Button>
                            <Button variant="outline" size="sm" className="flex-1" asChild>
                              <Link href={`/community/groups/${group.id}/settings`}>
                                <Settings className="w-4 h-4 mr-1" />
                                관리
                              </Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">만든 모임이 없습니다</h3>
                    <p className="text-muted-foreground mb-4">
                      첫 모임을 만들어보세요!
                    </p>
                    <Button asChild>
                      <Link href="/community/groups/new">
                        <Plus className="w-4 h-4 mr-2" />
                        모임 만들기
                      </Link>
                    </Button>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
